// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import CeldaVence from "../components/funcionales/CeldaVence";
import ConsultarCENT, {
  useConsultasCENT,
} from "../components/funcionales/ConsultarCENT";
import Ubicacion from "../components/funcionales/Ubicacion";
import Patente from "../components/funcionales/Patente";
import { ruta as rutaDeUnidad } from "../shared/unidad";
import {
  actividadDe,
  declararRTO,
  estadoRTO,
  furgonesRTO,
} from "../services/porteria";
import { useRealtime } from "../services/store";
import { etiquetaLona } from "../shared/lona";
import { etiquetaPuerta } from "../shared/puerta";
import { etiquetaTipoTaller, sinReconocer } from "../shared/tipoTaller";
import {
  etiquetaCorta as empresaCorta,
  etiquetaCortaConDuda as empresaConDuda,
  nombreDe as empresaNombre,
} from "../shared/empresa";
import Casilla from "../components/funcionales/Casilla";
import TablaUnidades from "../components/tablas/TablaUnidades";
import {
  FILTROS,
  conActividad,
  contar,
  cuando,
  etiquetaDe,
  fecha,
  filtrar,
  puntoDe,
} from "../shared/rto";
import { sePuedeDeclarar, soloCampos } from "../shared/rtoDeclarada";
import { puedeConsultarCENT, sePuedeConsultar } from "../shared/rtoCent";
import Loading from "./Loading";
import usePolizas from "../hooks/usePolizas";
import { celdaSeguro, puedeVerSeguro } from "../shared/seguroFicha";

/* El RTO de los furgones.
 *
 * POR QUÉ ES UN LISTADO Y NO UNA FICHA COMO LA DE TRACTORES: de los furgones
 * no sabemos nada más. No tienen equipo satelital y no tienen kilómetros, así
 * que no hay ficha con posición y proyección que armar. El RTO es el dato
 * central de esta pantalla y por eso es lo que la ordena.
 *
 * ── LO QUE SÍ SE SABE, Y QUE ACÁ DECÍA QUE NO ────────────────────────────
 *
 * Este comentario decía que "no se puede saber cuál está llegando a base".
 * Era una conclusión de más sacada de una premisa cierta: no hay GPS, pero
 * **no hay GPS no es no hay registro**. Portería anota cada entrada y cada
 * salida del portón —y lo anota HOY, mientras el movimiento de unidades viene
 * diez días atrasado—, y el movimiento sabe a dónde fue. Entre las dos ubican
 * a 593 de los 679 furgones del padrón.
 *
 * Por eso ahora hay una columna de ubicación. Nunca sale sola: va con la fecha
 * al lado, porque «Río Grande» sin fecha se lee como «está ahí ahora» y puede
 * ser de hace tres semanas. La regla vive en `ubicaciones_ultimas` (la base) y
 * en `shared/ubicacion.js` (cómo se dice).
 *
 * DOS PESTAÑAS, y la división no es cosmética:
 *
 *   · Vencimientos — lo accionable. Ordenado por urgencia: primero lo que ya
 *     venció hace poco y lo que está por vencer.
 *   · Vencidos hace mucho — más de 90 días. Están aparte porque son otra
 *     conversación: en su enorme mayoría son unidades que ya no trabajan y que
 *     el padrón sigue dando por activas. Mezclarlos con los de arriba tapa lo
 *     único sobre lo que alguien va a hacer algo esta semana.
 */

const DIAS_VIEJO = 90;

const Furgones = () => {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState("");
  const [vista, setVista] = useState("vencimientos");
  /* Arranca en «todos» y no en «vencido». El corte de arriba —vencimientos vs.
   * vencidos hace mucho— ya dejó adelante lo accionable; abrir además filtrado
   * escondería filas sin que nadie lo haya pedido, y el que no se dio cuenta
   * lee una lista corta y se queda tranquilo. */
  const [estado, setEstado] = useState("todos");
  const [texto, setTexto] = useState("");

  /* ── EL LÁPIZ: la fecha que viene en el TÍTULO ──────────────────────────
   *
   * Un 0 km no rinde RTO por un año, y esta lista los mostraba como «Sin RTO
   * registrada» — igual que un acoplado al que le falta el papel. Son **66
   * furgones activos**, 53 de ellos 2025/2026.
   *
   * Todo esto es el gemelo de lo que hace `/rto` con los tractores, y por eso
   * la celda es el MISMO componente: con dos, el día que cambie cómo se marca
   * una fecha declarada las dos pantallas dirían cosas distintas del mismo
   * tipo de dato. */
  const { permisos = null } = useOutletContext() || {};
  const puedeCargar = permisos === null || permisos.includes("cargar.documentos");
  const [lapiz, setLapiz] = useState(false);

  /* Se vuelve a pedir ESE furgón y se le pegan los campos encima, en su lugar.
   * Recalcular el estado acá sería una segunda definición de cuándo un
   * acoplado puede salir; volver a traer los 679 haría saltar la fila justo
   * cuando se están cargando de a 66 seguidos. */
  const guardarDeclarada = useCallback(async (dominio, vence, motivo) => {
    await declararRTO(dominio, vence, motivo);
    const fresco = await estadoRTO(dominio);
    setFilas((fs) =>
      (fs || []).map((f) =>
        f.dominio === dominio ? { ...f, ...soloCampos(fresco) } : f,
      ),
    );
  }, []);

  /* ── CONSULTAR EL CENT AHORA, de a un furgón ────────────────────────────
   *
   * El caso que lo trajo es de esta pantalla: el furgón 448 hizo la VTV al
   * mediodía del 09/10 y seguía figurando con lo consultado a las 04:35 (mail
   * de Aldo, ticket #371). Mismo botón y mismo criterio que `/rto`: consulta
   * el bot, y lo que vuelve se pega en la fila sin recalcular nada acá. */
  const puedeConsultar = puedeConsultarCENT(permisos);
  /* La póliza de cada unidad (Aldo, 23/10, ticket 495). Con el mismo permiso
   * que la línea «Seguro» de la ficha; sin él, o si no se pudo leer, la
   * columna no está. */
  const verSeguro = puedeVerSeguro(permisos);
  const polizas = usePolizas(verSeguro);
  const pegarEstado = useCallback((dominio, fresco) => {
    const campos = soloCampos(fresco);
    setFilas((fs) =>
      fs && fs.map((f) => (f.dominio === dominio ? { ...f, ...campos } : f)),
    );
  }, []);
  const cent = useConsultasCENT(pegarEstado);

  /* LA LONA SE CRUZA DESDE EL PADRÓN, QUE YA ESTÁ EN MEMORIA.
   *
   * `rto_furgones()` contesta por patente y no sabe de la lona; el armazón ya
   * tiene el padrón cargado para los números del menú. Cruzar acá cuesta un
   * `Map` y no un viaje de red, y sobre todo deja UNA sola definición del
   * dato: si se agregara a la función de la base, el día que alguien corrija
   * una lona desde el padrón esta pantalla seguiría mostrando lo viejo hasta
   * la próxima consulta. */
  const padron = useRealtime("furgones");
  const delPadron = useMemo(() => {
    const m = new Map();
    for (const f of padron || []) {
      // El padrón guarda el interno en `id` (herencia de Firestore) y el RTO
      // lo llama `interno`. Se compara como texto: uno viene número y el otro
      // string, y `Map` no los da por iguales.
      if (f?.id != null) m.set(String(f.id), f);
    }
    return m;
  }, [padron]);

  /* Cuándo se vio moverse a cada acoplado, para el tag «Sin uso?». Va aparte y
   * no bloquea: si no llega, el tag no se enciende y la pantalla dice lo de
   * siempre. `conActividad` además apaga la regla entera si las fuentes de
   * actividad están frías, para que un corte de datos no vacíe la lista de
   * vencidos y eso pase por una buena noticia. */
  const [actividad, setActividad] = useState(null);

  useEffect(() => {
    let vigente = true;
    furgonesRTO()
      .then((f) => vigente && setFilas(f))
      .catch((e) => vigente && setError(e.message || "No se pudo cargar."));
    /* Envuelto y con el rechazo tragado: el tag es un AGREGADO y no puede
     * tirar abajo la lista de RTO. Ver el mismo bloque en `RTO.jsx`. */
    Promise.resolve(actividadDe("furgon")).then(
      (a) => vigente && setActividad(a),
      () => {},
    );
    return () => {
      vigente = false;
    };
  }, []);

  const { vencimientos, viejos } = useMemo(() => {
    /* Las solapas parten por HACE CUÁNTO venció; el tag, por hace cuánto que no
     * se la ve. Son dos preguntas distintas sobre la misma fila: un acoplado
     * puede estar vencido hace tres años y haber salido la semana pasada. */
    const todos = conActividad(filas, actividad);
    const esViejo = (f) =>
      f.estado === "vencida" && f.dias !== null && f.dias < -DIAS_VIEJO;
    return {
      vencimientos: todos.filter((f) => !esViejo(f)),
      viejos: todos.filter(esViejo),
    };
  }, [filas, actividad]);

  /* El filtro busca por patente Y por interno porque las dos se usan: la
   * patente está en el papel del acoplado, el interno es como lo llaman todos.
   * Se ignoran guiones y espacios de la patente — nadie los escribe igual. */
  const deLaSolapa = vista === "viejos" ? viejos : vencimientos;
  /* Los conteos se cuentan sobre la SOLAPA y no sobre todo: un chip que dice
   * «Vencido · 540» arriba de una lista de 139 filas se lee como que la
   * pantalla filtró mal. */
  const cuentas = useMemo(() => contar(deLaSolapa), [deLaSolapa]);

  const visibles = useMemo(() => {
    const base = filtrar(deLaSolapa, estado);
    const q = texto.trim().toLowerCase();
    if (!q) return base;
    const limpio = q.replace(/[^a-z0-9]/g, "");
    /* Y LA BÚSQUEDA LE GANA AL FILTRO, igual que en los padrones: buscar una
     * patente es preguntar por un acoplado concreto, y contestar "no hay
     * resultados" porque el chip lo tapaba es el peor no que puede dar un
     * buscador — el que busca ya sabe que existe. */
    const donde = q ? deLaSolapa : base;
    return donde.filter(
      (f) =>
        (f.dominio || "").toLowerCase().replace(/[^a-z0-9]/g, "").includes(limpio) ||
        String(f.interno || "").toLowerCase().includes(q)
    );
  }, [deLaSolapa, estado, texto]);

  /* LAS COLUMNAS SON UN DATO Y NO JSX SUELTO, porque las lee `TablaUnidades`
   * para armar además el filtro de cada una. `valor` es lo que se filtra Y lo
   * que se agrupa; `celda` es cómo se dibuja. Van separados justamente para
   * que el desplegable diga «Sí · 12» y la celda pinte la casilla verde: con
   * una sola función, elegir «Sin dato» podría no traer las filas que dicen
   * «Sin dato».
   *
   * ESTADO NO LLEVA FILTRO DE COLUMNA, a propósito: ya lo filtran los chips de
   * arriba, que además traen la cuenta. Con los dos, alguien puede poner el
   * chip en «Vencido» y la columna en «Al día», ver cero filas y no entender
   * por qué — dos controles para el mismo hecho es de lo que esta pantalla ya
   * se cuidaba. */
  const columnas = useMemo(() => {
    const del = (f) => delPadron.get(String(f.interno)) || {};
    return [
      {
        clave: "interno",
        titulo: "Interno",
        /* EL INTERNO ES LA PUERTA A LA FICHA. Sin interno no hay enlace: la
         * ficha va por número y `/unidad/furgon/` no lleva a ningún lado. */
        celda: (f) =>
          f.interno ? (
            <Link to={rutaDeUnidad("furgon", f.interno)} className="rto-link">
              {f.interno}
            </Link>
          ) : (
            "—"
          ),
      },
      {
        clave: "dominio",
        titulo: "Patente",
        /* La patente como chapa Mercosur (Igna, 03/10: «quedan re facheras»).
         * Salvo en `sin_patente`: ahí el campo tiene el INTERNO copiado
         * ('1500', '1501'…), y dibujarlo como chapa lo haría pasar por una
         * patente de verdad — justo lo que el aviso de arriba manda a
         * corregir. Ése va como texto, como estaba. */
        celda: (f) =>
          f.dominio && f.estado !== "sin_patente" ? (
            <Patente dominio={f.dominio} />
          ) : (
            f.dominio || "—"
          ),
        clase: () => "dato",
      },
      {
        clave: "empresa",
        titulo: "Empresa",
        filtro: true,
        /* La sigla, que es lo que se pidió y lo que entra en una celda. El
         * nombre largo va en el `title`: nadie reconoce a Expreso Cantarini
         * por su CUIT, pero «EX» tampoco lo dice todo la primera vez. */
        valor: (f) => empresaCorta(f.empresa),
        celda: (f) => (
          <span title={empresaNombre(f.empresa)}>{empresaConDuda(f.empresa)}</span>
        ),
        clase: (f) => (empresaCorta(f.empresa) === "—" ? "tenue" : undefined),
      },
      {
        clave: "marca",
        titulo: "Marca",
        filtro: true,
        valor: (f) => f.marca || f.modelo || "—",
        clase: () => "tenue",
      },
      {
        clave: "tipo",
        titulo: "Tipo",
        filtro: true,
        valor: (f) => etiquetaTipoTaller(del(f).tipo_taller),
        clase: (f) =>
          etiquetaTipoTaller(del(f).tipo_taller) === "Sin dato" ? "tenue" : undefined,
      },
      {
        clave: "lona",
        titulo: "Lona",
        filtro: true,
        valor: (f) => etiquetaLona(del(f).lona),
        celda: (f) => <Casilla valor={del(f).lona} que="Lona" />,
      },
      {
        clave: "puerta",
        titulo: "Puerta",
        filtro: true,
        valor: (f) => etiquetaPuerta(del(f).puerta),
        celda: (f) => <Casilla valor={del(f).puerta} que="Puerta" />,
      },
      {
        clave: "estado",
        titulo: "Estado",
        /* `etiquetaDe` y no `ETIQUETA` directo: «Sin uso?» no sale del CENT,
         * así que el servidor nunca lo manda. Leyendo el estado crudo, filtrar
         * por «Sin uso?» daría una lista de filas que dicen «Vencido». */
        valor: (f) => etiquetaDe(f),
        celda: (f) => (
          <>
            <span className={"rto-punto rto-punto--" + puntoDe(f)} />
            {etiquetaDe(f)}
            {/* Un RTO vigente pero Condicional no es lo mismo que uno Apto, y
              * con sólo la fecha eso se pierde. */}
            {f.resultado && f.resultado !== "Apto" && (
              <strong className="rto-resultado"> · {f.resultado}</strong>
            )}
          </>
        ),
      },
      {
        clave: "vence",
        titulo: "Vence",
        /* El valor que se FILTRA sigue siendo la fecha que manda, prendido el
         * lápiz o no: con el filtro mirando otra cosa, elegir «12/03/2027» no
         * traería las filas que dicen 12/03/2027. */
        valor: (f) => fecha(f.vence),
        celda: (f) => (
          <CeldaVence
            fila={f}
            editando={lapiz && puedeCargar}
            onGuardar={guardarDeclarada}
          />
        ),
      },
      {
        clave: "cuando",
        titulo: "Cuándo",
        valor: cuando,
        clase: (f) => (f.dias < 0 ? "rto-tarde" : undefined),
      },
      /* El seguro, al lado del RTO: son las dos preguntas de «¿puede salir?».
       * «Sin cargar» cuando no hay fila; «Sin seguro» sólo cuando Seguros lo
       * dijo (`estado` «baja», ver `seguroFicha`). */
      ...(verSeguro && polizas
        ? [
            {
              clave: "seguro",
              titulo: "Seguro",
              filtro: true,
              valor: (f) => celdaSeguro(polizas, f.dominio).valor,
              celda: (f) => celdaSeguro(polizas, f.dominio).texto,
              clase: (f) => {
                const s = celdaSeguro(polizas, f.dominio);
                return s.aviso ? "rto-tarde" : s.neutro ? "tenue" : undefined;
              },
            },
          ]
        : []),
      /* Al lado de «Cuándo», como en `/rto`. Sin el permiso no está. */
      ...(puedeConsultar
        ? [
            {
              clave: "cent",
              titulo: "CENT",
              celda: (f) =>
                sePuedeConsultar(f) ? (
                  <ConsultarCENT
                    dominio={f.dominio}
                    consulta={cent.de(f.dominio)}
                    onConsultar={cent.consultar}
                  />
                ) : (
                  "—"
                ),
              clase: (f) => (sePuedeConsultar(f) ? undefined : "tenue"),
            },
          ]
        : []),
      {
        clave: "ubicacion",
        titulo: "Última ubicación registrada",
        filtro: true,
        /* Se filtra por el LUGAR y se dibuja el componente entero, que suma la
         * fecha y la fuente. El lugar nunca sale solo — «Río Grande» sin fecha
         * se lee como «está ahí ahora» y puede ser de hace tres semanas. */
        valor: (f) => f.ubic_lugar || "—",
        celda: (f) => <Ubicacion fila={f} />,
      },
      {
        clave: "detalle",
        titulo: "Detalle",
        /* Sin filtro: es texto libre, así que el desplegable tendría una
         * opción por fila y no ordenaría nada. */
        valor: (f) => del(f).detalle || "",
        clase: () => "tenue",
      },
      {
        clave: "observaciones",
        titulo: "Observaciones",
        /* La nota del taller (Federico, 23/09): «patas rotas», «sin techo».
         * Se carga desde `/FurgonesApp` y acá se ve — un dato que se carga y
         * nadie mira se deja de cargar. Sin filtro, es texto libre. */
        valor: (f) => del(f).observaciones || "",
        clase: (f) => (del(f).observaciones ? "sub" : "tenue"),
      },
    ];
  }, [delPadron, lapiz, puedeCargar, guardarDeclarada, puedeConsultar, cent,
      verSeguro, polizas]);

  /* Los tipos que no están en las sugerencias, para mostrarlos. Se miran sobre
   * el PADRÓN entero y no sobre la solapa: un tipo mal escrito en un furgón
   * que hoy no está en esta lista hay que corregirlo igual. */
  const tiposRaros = useMemo(() => sinReconocer(padron || []), [padron]);

  if (error) {
    return (
      <section className="tk-detalle tk-detalle--vacio">
        <p>{error}</p>
      </section>
    );
  }
  if (!filas) return <Loading />;

  const sinConsultar = filas.filter((f) => f.estado === "sin_consultar").length;
  const sinPatente = filas.filter((f) => f.estado === "sin_patente").length;
  /* Cuántos de la solapa esperan la fecha del título. Va sobre la SOLAPA, como
   * el resto de los conteos: un «✏️ · 66» arriba de una lista de 12 se lee
   * como que la pantalla filtró mal. */
  const porCargar = deLaSolapa.filter(
    (f) => sePuedeDeclarar(f) && !f.declarada,
  ).length;

  return (
    <div className="rto-furgones">
      <div className="tk-filtros">
        <button
          className={"tk-chip" + (vista === "vencimientos" ? " activo" : "")}
          onClick={() => setVista("vencimientos")}
        >
          Vencimientos ({vencimientos.length})
        </button>
        <button
          className={"tk-chip" + (vista === "viejos" ? " activo" : "")}
          onClick={() => setVista("viejos")}
        >
          Vencidos hace más de 90 días ({viejos.length})
        </button>
        {/* LA AGENDA ES UNA SOLA Y ESTÁ EN LA OTRA PANTALLA.
         *
         * Los turnos de RTO viven en `rto_turnos`, que va por PATENTE y no
         * distingue tractor de furgón — así que la agenda ya servía para los
         * acoplados y desde acá no había ninguna forma de llegar. Un enlace y
         * no una segunda agenda dibujada acá: dos pantallas que hacen lo mismo
         * obligan a adivinar en cuál se carga, y el día que se acierte la que
         * no es, ese turno no aparece en ningún lado.
         *
         * Y es un enlace de verdad y no un botón con `navigate`: se puede
         * abrir en otra pestaña, que es lo que uno hace cuando está mirando
         * una lista y quiere chequear otra cosa sin perderla. */}
        <Link className="tk-chip" to="/rto?vista=agenda">
          Agenda de turnos ↗
        </Link>
      </div>

      {/* El estado, adentro de la solapa. Son dos preguntas distintas: la
          solapa separa lo accionable de lo que ya no lo es, y esto dice en qué
          punto está cada uno. */}
      <div className="tk-filtros tk-filtros--sub">
        {FILTROS.map(([clave, nombre]) => (
          <button
            key={clave}
            className={"tk-chip" + (estado === clave ? " activo" : "")}
            onClick={() => setEstado(clave)}
          >
            {nombre} · {cuentas[clave]}
          </button>
        ))}
        {/* EL LÁPIZ, y sólo cuando hay algo que cargar. Un botón que no cambia
            nada visible —porque los 679 ya tienen fecha del CENT— se aprende a
            ignorar. Con el número al lado dice además cuánto trabajo hay. */}
        {puedeCargar && porCargar > 0 && (
          <button
            className={"tk-chip" + (lapiz ? " activo" : "")}
            onClick={() => setLapiz((v) => !v)}
            title="Los 0 km no rinden RTO por un año: la fecha viene en el título."
          >
            ✏️ Fecha del título · {porCargar}
          </button>
        )}
      </div>

      <input
        className="permisos-buscar"
        type="search"
        placeholder="Buscar por patente o interno…"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        aria-label="Buscar furgón por patente o interno"
      />

      {/* Que no se pueda consultar NO es lo mismo que no tener RTO, y si esto
        * no se dijera, una falla de sincronización se leería como una flota
        * sin revisión. Se avisa arriba, no al pie. */}
      {sinConsultar > 0 && (
        <p className="rto-aviso">
          {sinConsultar} {sinConsultar === 1 ? "furgón todavía no entró" : "furgones todavía no entraron"}{" "}
          en la sincronización.
        </p>
      )}

      {/* Y esto NO es lo mismo que lo de arriba. Estos furgones tienen el
        * interno copiado en el campo de la patente ('1500', '1501'…), así que
        * no hay nada que consultar y nunca lo va a haber. Decirles "todavía no
        * lo miramos, se actualiza todas las noches" es hacer esperar por algo
        * que no pasa solo: se arregla cargando la patente en CantApp. */}
      {sinPatente > 0 && (
        <p className="rto-aviso">
          {sinPatente} {sinPatente === 1 ? "furgón no tiene" : "furgones no tienen"}{" "}
          patente cargada en el padrón.
        </p>
      )}

      {/* LO QUE NO SE RECONOCE SE MUESTRA. El tipo es vocabulario abierto —se
        * carga desde `/FurgonesApp` y acepta lo que no está en las
        * sugerencias— y sin esta lista se pudre solo: «pincén» con acento
        * sería otro tipo y nadie se enteraría. No se fusionan: se muestran.
        * Misma idea que `zona.desconocidas` y `permisos_desconocidos()`. */}
      {tiposRaros.length > 0 && (
        <p className="rto-aviso rto-aviso--suave">
          Tipos que no están en la lista:{" "}
          {tiposRaros.map((t) => `${t.clave} (${t.n})`).join(" · ")}.
        </p>
      )}

      <TablaUnidades
        columnas={columnas}
        filas={visibles}
        vacio={
          texto
            ? "Ningún furgón con esa patente o interno."
            : vista === "viejos"
            ? "Ninguno vencido hace más de 90 días."
            : "Ningún furgón cargado."
        }
      />
    </div>
  );
};

export default Furgones;
